// The message stream: grouped like Discord, day dividers, stays pinned to the bottom while you're there,
// keeps your place when older messages load above. Hover a message for its bar: the 3 emoji you used last, the full
// picker, reply,
// reply in thread, and More (everything else, MessageMenu.tsx). Only the rows on
// screen (and a margin around them) are drawn; see MessageList.
// Opening a channel with unread messages shows a "NEW" line above the first one (scrolled into view if it would be
// off screen); scrolled up away from the bottom, a "Jump to present" pill sits above the composer.
import { Fragment, memo, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from "react";
import { applyEmoticons, BLOOM_ID, isBotId, CUSTOM_EMOJI_TOKEN, decodeChannelRefs, decodeCustomEmoji, decodeMentions, encodeCustomEmoji, encodeMentions, EVERYONE, hasMarkdown, markdownToPlain, MENTION_TOKEN, mentionPicks, mentionsUser, shortLinkLabel, splitLinksShort, SPOILER_MASK, type Attachment, type Embed, type Forward } from "@bloom/shared";
import { textToSend } from "../stripTracking.ts";
import { ForwardIcon } from "./ForwardDialog.tsx";
import { linkPreviewsIn, messageLinkFor, messageLinkLabel, MessageLinkTextContext, type MessageLinkHost, type MessageLinkText } from "./MessageLinks.ts";
import { bubbleStamp, parseMessageLink, startsGroup, type MessageLinkRef } from "@bloom/shared";
import "./forward.css";
import { formatStamp, Markdown } from "./Markdown.tsx";
import { formatShortcut } from "../formatKeys.ts";
import { FormatLayer } from "./FormatLayer.tsx";
import { FormatBar } from "./FormatBar.tsx";
import { MAX_UNREAD_JUMP, type ChatMessage } from "../realtime.ts";
import { fileUrl, formatBytes, isImage, isVideo } from "../upload.ts";
import { Avatar } from "./ui.tsx";
import { EmojiPicker } from "./EmojiPicker.tsx";
import { thudForLanding, useJumboLandings, useReactionEffectsOn } from "../reactionEffects.ts";
import { useChipHold, type ChipHold } from "./useChipHold.tsx";
import { useQuickHold, type QuickHeld, type QuickHold } from "./useQuickHold.tsx";
import { Embeds } from "./Embeds.tsx";
import { Attachments, type OpenAs, type ViewerItem } from "./Attachments.tsx";
import { clampPage, pageLabel } from "../lightboxLogic.ts";
import { useViewerGestures } from "../lightboxGestures.ts";
import { useBackCloses } from "../backCloses.ts";
import { download, downloadables } from "../download.ts";
import { Sheet } from "./Sheet.tsx";
import { emojiReady, loadEmoji, noteEmojiUse } from "../emoji.ts";
import { ACTION_GROUPS, MENU_ICONS, MoreIcon, MoreMenu, type ActionKey } from "./MessageMenu.tsx";
import { useMessageDisplay } from "../density.ts";
import { useTextSize } from "../textSize.ts";
import { isEditable, isTouch, longPress } from "../mobile.ts";
import { useShortLinks } from "../shortLinks.ts";
import { LinkMenu, openLink, ShortLinkContext, showLabel, takeTouchedLink, touchLink, type LinkTarget } from "./ShortLinks.tsx";
import { createPortal } from "react-dom";
import { ChevronIcon, CloseButton, InfoIcon, RoundButton } from "./icons.tsx";
import { cardHeight, customEmojiId } from "@bloom/shared";
import { MessageCard, narrowCards, type CardHost } from "./AppCard.tsx";
import { ReactionGlyph, reactionLabel, useServerEmoji, useUsedHere } from "../customEmoji.tsx";
import { ReactionTooltip, TIP_DELAY_MS, type ReactionTip } from "./ReactionTooltip.tsx";
import { ReactionSheet } from "./ReactionSheet.tsx";
import { PINS, type ListActor, type ListInfo, type MessageDisplay } from "@bloom/shared";
import { ListBadges, ListGlyph, ListPicker, namedLists, noteListUse, PinIcon, SystemLine } from "./Lists.tsx";
import { useChatLayout } from "../layout.ts";
import { NameColorsContext, nameLook } from "../coloredNames.ts";
import { chatsMessages } from "./chats/ChatsMessage.tsx";
import { classicMessages } from "./classic/ClassicMessage.tsx";
import { ircMessages } from "./irc/IrcMessage.tsx";
import type { MessageLayout, RowItem } from "./MessageLayout.ts";

/**
 * THE switch for message rows (#141): the message display in use picks what its rows look like (IRC layout has its own).
 * The list (windowing, scroll anchoring, the NEW line) and everything a row holds besides its frame are shared.
 */
const MESSAGE_LAYOUTS: Record<MessageDisplay, MessageLayout> = {
  cozy: classicMessages,
  compact: classicMessages,
  bubbles: chatsMessages,
};

/** What the list needs for lists and pins: badges, the hover buttons, the picker, the quiet lines. */
export interface ListHost {
  serverId: string;
  /** Every list I can see, and by id. */
  all: ListInfo[];
  byId: Map<string, ListInfo>;
  /** Changes whenever what any message is in changes (rows re-draw their badges). */
  version: unknown;
  /** The list ids a message is in that I can see (shared and my private ones). */
  listsOf: (m: ChatMessage) => string[];
  /** This conversation is a DM (its messages can't go in shared lists). */
  inDm: boolean;
  /** Who I am to the list rules (shared/src/lists.ts): locked lists I can't add to aren't offered. */
  actor: ListActor;
  /** The pins list of this conversation's channel, if anything was ever pinned. */
  pins: ListInfo | undefined;
  toggle: (m: ChatMessage, listId: string, on: boolean) => void;
  /** Makes a list and puts the message in it; resolves with an error or null. */
  create: (m: ChatMessage, name: string, visibility: "server" | "private") => Promise<string | null>;
  open: (listId: string) => void;
}

const NO_EMOJI = new Map<string, never>();
/** When a forwarded message was first posted: "Oct 23, 3:04 PM". */
const forwardFmt = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const dayKey = (ts: number) => new Date(ts).toDateString();

function ago(ts: number) {
  const s = Math.max(0, (Date.now() - ts) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

/** Channel links and lookups for channel references in message text. */
export interface ChannelRefs {
  /** name → id (typed "#name" in a message not yet confirmed by the room). */
  byName: Map<string, string>;
  /** id → current name (stored "<#id>"). */
  byId: Map<string, string>;
  open: (channelId: string) => void;
}

/** What message text needs to draw @mentions: stored "<@id>" tokens become chips with the person's current name. */
interface People {
  /** Highlights a typed "@Name" that isn't a token (older messages, or a name two members share). */
  re: RegExp | null;
  names: Map<string, string>;
  me: string | null;
}

/** Plain text with "<@id>" tokens drawn as chips (stronger for me and @everyone) and typed "@Name" highlighted. */
function Mentions({ text, people }: { text: string; people: People }) {
  const colors = useContext(NameColorsContext);
  if (!text.includes("@")) return <>{text}</>;
  const tokens = text.includes("<@") ? [...text.matchAll(MENTION_TOKEN)] : [];
  if (tokens.length) {
    const out: React.ReactNode[] = [];
    let last = 0;
    for (const m of tokens) {
      const id = m[1]!;
      const name = id === EVERYONE ? EVERYONE : people.names.get(id);
      if (m.index! > last) out.push(<Mentions key={`t${last}`} text={text.slice(last, m.index)} people={people} />);
      // Colored names (#550): another member's pill wears their color. My own pill, @everyone and a gone user keep their look.
      const plain = !name || id === EVERYONE || id === people.me;
      const look = plain ? {} : nameLook(colors, id);
      const cls = !name ? "mention gone" : id === EVERYONE || id === people.me ? "mention me" : "mention";
      out.push(<span key={`m${m.index}`} className={look.className ? `${cls} ${look.className}` : cls} style={look.style} data-user={id}>@{name ?? "unknown-user"}</span>);
      last = m.index! + m[0].length;
    }
    if (last < text.length) out.push(<Mentions key={`t${last}`} text={text.slice(last)} people={people} />);
    return <>{out}</>;
  }
  const { re } = people;
  if (!re) return <>{text}</>;
  return <>{text.split(re).map((part, i) => (i % 2 ? <span key={i} className="mention">{part}</span> : <Fragment key={i}>{part}</Fragment>))}</>;
}

// A stored reference "<#id>", or a typed "#name" (after a space or opening punctuation) in a message still sending.
const CHANNEL_REF = /<#([0-9A-Za-z]{10,40})>|(^|[\s(\[{"'])#([a-z0-9][a-z0-9-]*)/gi;

/** Plain text with #channel turned into links (only channels that exist) and @mentions highlighted. */
function Rich({ text, people, channels }: { text: string; people: People; channels?: ChannelRefs }) {
  if (!channels || !text.includes("#")) return <Mentions text={text} people={people} />;
  const out: React.ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(CHANNEL_REF)) {
    let id: string | undefined;
    let name: string;
    let start: number;
    let length: number;
    if (m[1]) {
      id = m[1];
      name = channels.byId.get(id) ?? "deleted-channel";
      if (!channels.byId.has(id)) id = undefined;
      start = m.index!;
      length = m[0].length;
    } else {
      // Trailing dashes are punctuation ("#general-").
      name = m[3]!.replace(/-+$/, "").toLowerCase();
      id = channels.byName.get(name);
      if (!id) continue;
      start = m.index! + m[2]!.length;
      length = 1 + name.length;
    }
    if (start > last) out.push(<Mentions key={`t${last}`} text={text.slice(last, start)} people={people} />);
    out.push(
      id ? (
        <button key={`c${start}`} type="button" className="channel-link" onClick={() => channels.open(id!)}>
          #{name}
        </button>
      ) : (
        <span key={`c${start}`} className="channel-link gone">#{name}</span>
      ),
    );
    last = start + length;
  }
  if (last < text.length) out.push(<Mentions key={`t${last}`} text={text.slice(last)} people={people} />);
  return <>{out}</>;
}

/** Message text with @mentions highlighted, #channels and links made clickable. */
function Body({ text, names, me, channels }: { text: string; names: Map<string, string>; me: string | null; channels?: ChannelRefs }) {
  const escaped = [...names.values()].map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).sort((a, b) => b.length - a.length);
  const re = names.size ? new RegExp(`(@(?:${escaped.join("|")}|Bloom|channel)\\b)`, "gi") : null;
  const people: People = { re, names, me };
  const shorten = useShortLinks();
  if (!/https?:\/\/|www\./i.test(text)) return <Rich text={text} people={people} channels={channels} />;
  return (
    <>
      {splitLinksShort(text, shorten).map((p, i) =>
        p.kind === "text" ? (
          <Rich key={i} text={p.text} people={people} channels={channels} />
        ) : (
          <LinkText key={i} href={p.href} text={p.text} short={"short" in p ? p.short : undefined} channels={channels} />
        ),
      )}
    </>
  );
}

/**
 * A link in message text. A link to a message on Bloom reads as a short chip, "Maya in #general", with the address
 * as its title; anything else shows as typed.
 */
function LinkText({ href, text, short, channels }: { href: string; text: string; short?: string; channels?: ChannelRefs }) {
  const ref = parseMessageLink(href, location.origin);
  if (!ref) {
    if (short) return <ShortLink href={href} text={text} short={short} />;
    return (
      <a href={href} target="_blank" rel="noreferrer noopener" className="msg-link">
        {text}
      </a>
    );
  }
  return <MessageLinkChip href={href} link={ref} channels={channels} />;
}

/**
 * A link with a long query string (#734), drawn short: "🔗 https://site/path?… ✂️". The click, the hover title and the menus
 * use the full link. "Show full URL" (right-click, or the phone's long-press sheet) swaps the short form for the full link in
 * this link only; the state is not stored. The no-break spaces and `word-break: break-all` (CSS) make it wrap as one string.
 */
function ShortLink({ href, text, short }: { href: string; text: string; short: string }) {
  const host = useContext(ShortLinkContext);
  const [full, setFull] = useState(false);
  const target: LinkTarget = { href, full, toggle: () => setFull((f) => !f) };
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className={`msg-link short-link${full ? " full" : ""}`}
      data-short={full ? "off" : "on"}
      title={full ? undefined : href}
      aria-label={full ? undefined : shortLinkLabel(href)}
      onContextMenu={
        host
          ? (e) => {
              // The browser's menu is replaced by ours (Open link, Copy link, Show full URL); a touch device uses the sheet.
              if (isTouch()) return;
              e.preventDefault();
              e.stopPropagation();
              host.menu(target, { x: e.clientX, y: e.clientY });
            }
          : undefined
      }
      onTouchStart={() => touchLink(target)}
    >
      {full ? text : short}
    </a>
  );
}

/**
 * "Maya in #general" once the room says I can see the message (MessageLinkTextContext, set by the list); before that,
 * or when it's gone or not mine to see, "#general › message", or just "message" (a DM, another server).
 */
function MessageLinkChip({ href, link, channels }: { href: string; link: MessageLinkRef; channels?: ChannelRefs }) {
  const ctx = useContext(MessageLinkTextContext);
  const [label, setLabel] = useState<string | null>(null);
  useEffect(() => {
    if (!ctx || link.serverId !== ctx.host.serverId) return;
    let live = true;
    void ctx.host.resolve(link.messageId).then((r) => {
      if (!live || !r) return;
      setLabel(messageLinkLabel(ctx.names.get(r.message.authorId) ?? "Someone", ctx.channelName(r.rootId), r.message.channelId !== r.rootId));
    });
    return () => {
      live = false;
    };
  }, [ctx, link.messageId]);
  const name = channels?.byId.get(link.channelId);
  return (
    <a href={href} target="_blank" rel="noreferrer noopener" className="msg-link msglink-chip" title={href}>
      {label ?? (name ? `#${name} › message` : "message")}
    </a>
  );
}

/** Big emoji when a message is only emoji (a server's own count too), like Discord. */
const onlyEmoji = (raw: string) => onlyUnicodeEmoji(raw.includes("<") ? raw.replace(CUSTOM_EMOJI_TOKEN, "\u{1F600}") : raw);
const onlyUnicodeEmoji = (text: string) => /^(?:\p{Extended_Pictographic}|\p{Emoji_Component}|‍|️|\s){1,24}$/u.test(text) && !/[0-9#*]/.test(text) && [...text.replace(/\s/g, "")].length <= 12;

/** Fits media into a box no bigger than maxW × maxH, keeping its shape. Known sizes mean no layout shift. */
function fit(w: number | undefined, h: number | undefined, maxW: number, maxH: number) {
  if (!w || !h) return { width: maxW, height: Math.round(maxW * 0.66) };
  const s = Math.min(1, maxW / w, maxH / h);
  return { width: Math.round(w * s), height: Math.round(h * s) };
}

interface Props {
  channelId: string;
  messages: ChatMessage[];
  names: Map<string, string>;
  me: string | null;
  more: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => void;
  /**
   * Loads older messages until one is held: the message after a read position (for the NEW line) or a message by
   * id (a quote to jump to). Resolves when done or when it can't get there.
   */
  onLoadBack?: (target: { seq: number } | { id: string }) => Promise<void>;
  onRetry: (id: string) => void;
  /** Delete on a failed send: takes it off the screen without sending. */
  onDiscard: (id: string) => void;
  /** superReact: a new reaction sent as a super reaction (#87). jumbo: sent as my jumbo reaction (#714), also one I already have. */
  onReact: (messageId: string, emoji: string, on: boolean, superReact?: boolean, jumbo?: boolean) => void;
  onReply: (m: ChatMessage) => void;
  /** Deletes for everyone. Shown only where canDelete says so (own messages, or any for owners and admins). */
  onDelete: (m: ChatMessage) => void;
  canDelete: (m: ChatMessage) => boolean;
  /** Removes a message's link previews for everyone (offered where canDelete says so). */
  onSuppressEmbeds?: (m: ChatMessage) => void;
  /** Removes one attachment (by attachmentRef) for everyone (offered where canDelete says so). */
  onRemoveAttachment?: (m: ChatMessage, ref: string) => void;
  /** Marks a picture in my own message private or not. */
  onSetAttachmentPrivate?: (m: ChatMessage, ref: string, on: boolean) => void;
  /** The message being edited inline (controlled by the page, so ↑ in the composer can start it). */
  editingId: string | null;
  onStartEdit: (m: ChatMessage) => void;
  onEndEdit: (m: ChatMessage, newBody: string | null) => void;
  /** Omitted inside a thread (threads don't nest). */
  onOpenThread?: (m: ChatMessage) => void;
  /** Mark the channel unread from this message (the menus, Alt+click). Omitted where read state isn't kept (threads). */
  onMarkUnread?: (m: ChatMessage) => void;
  /** Opens the forward picker for a message (More menu, phone sheet). Omitted: no Forward. */
  onForward?: (m: ChatMessage) => void;
  /** A forwarded block's "from #channel": go to the original (if I can see it). */
  onOpenForward?: (f: Forward) => void;
  empty: string;
  /** Above the oldest message once there's nothing older (default: "This is the start of the channel."). */
  start?: string;
  /** Shown above the first message (a thread shows its parent here). */
  header?: React.ReactNode;
  /** Makes #channel in messages a link. */
  channels?: ChannelRefs;
  /** "@everyone" works here (text channels and threads, not DMs); used when an edit re-encodes mentions. */
  everyone?: boolean;
  /** A 1:1 DM: the other person's name isn't repeated above their bubbles. */
  oneToOne?: boolean;
  /** Read position when the channel was opened: the "NEW" line goes above the first message after it. null: none. */
  unreadAfter?: number | null;
  /** The words on the line instead of "NEW": the away seam's "New since 2:41 PM · 12" (#403). */
  unreadLabel?: string;
  /**
   * Counts up each time the page moves the line to a new away seam (#403): the list then brings the line into view if
   * it is off screen. A value that has not changed since the list was drawn does nothing.
   */
  seamTick?: number;
  /**
   * The page reads this when the person goes away (#403): the seq of the newest message seen, which is the list's end,
   * or the last row in view when scrolled up.
   */
  seenProbe?: React.MutableRefObject<(() => number) | null>;
  /** The channel's newest seq: with too many unread, opening it doesn't load back to the NEW line. */
  lastSeq?: number;
  /** My live read position: messages after it are counted on the jump pill ("3 new messages"). */
  readSeq?: number;
  /**
   * Told after every scroll or update whether the list is at the bottom, and the newest message it holds there
   * (the page marks the channel read up to it while the tab is visible). Not called before the first messages show.
   */
  onAtBottom?: (channelId: string, atBottom: boolean, newestSeq: number) => void;
  /** Draws app cards in messages (and keeps their live data). Stable between renders. */
  cards?: CardHost;
  /** Below the last message, outside the windowed rows: my private cards for this conversation. */
  footer?: React.ReactNode;
  /** Blooming threads' chips under their first messages. */
  blooming?: BloomRows;
  /** Lists and pins (badges, hover buttons, quiet lines). Omitted: none. */
  lists?: ListHost;
  /** Message links: Copy message link, previews of linked messages, and opening them in the app. Omitted: none. */
  links?: MessageLinkHost;
  /** Bring this message into view (from a list's Jump). `n` makes a repeat request count. */
  jumpRequest?: { id: string; n: number } | null;
}

/** Within this of the bottom counts as "at the bottom" (stays pinned, reads the channel). */
const BOTTOM_PX = 80;
/** Scrolled further up than this shows the "Jump to present" pill. */
const AWAY_PX = 300;
/** Rows are drawn this far beyond the visible area, above and below, so a quick flick never shows blank space. */
const OVERSCAN_PX = 900;
/** Opening a channel on its NEW line puts the line this far below the top. */
const NEW_LINE_TOP = 56;
/** One message as the list draws it. */
type Item = RowItem;

/** A link preview's height, guessed. A link to a picture (#688) is the picture alone, at the size its header gave. */
function embedPx(e: Embed): number {
  if (e.provider === "image" && e.image) return fit(e.image.width, e.image.height, 400, 320).height + 6;
  return e.image || e.video || e.gallery?.length ? 360 : 120;
}
/**
 * A guess at the height of a row we haven't drawn yet, so the scrollbar and the space above and below the drawn
 * rows are about right. Media have stored sizes; text is guessed from its length. Drawn rows are measured, and
 * the view is kept steady when a guess turns out wrong (see MessageList).
 */
function estimate(it: Item, width: number, L: MessageLayout, compact = false): number {
  const { m } = it;
  let h = L.headPx(it, compact) + L.stampPx(it);
  if (it.unread) h += 21;
  if (m.replyTo) h += L.replyPx;
  if (m.deleted) h += 20;
  if (m.forward && !m.deleted) {
    const f = m.forward;
    h += 44 + 20 * Math.ceil(f.body.length / Math.max(16, (width - 110) / 7.4));
    for (const a of f.attachments ?? []) h += a.kind === "file" && (isImage(a.type) || isVideo(a.type)) ? fit(a.width, a.height, 400, 320).height + 6 : a.kind === "gif" ? fit(a.width, a.height, 300, 260).height + 6 : a.kind === "sticker" ? 166 : 62;
    for (const e of f.embeds ?? []) h += embedPx(e);
  }
  if (m.body) {
    if (onlyEmoji(m.body)) h += 44;
    else h += L.textPx(m.body, width);
  }
  for (const a of m.attachments ?? []) {
    if (a.kind === "sticker") h += 166;
    else if (a.kind === "gif") h += fit(a.width, a.height, 300, 260).height + 6;
    else if (isImage(a.type) || isVideo(a.type)) h += fit(a.width, a.height, 400, 320).height + 6;
    else h += 62;
  }
  for (const e of m.embeds ?? []) h += embedPx(e);
  if (m.reactions && Object.keys(m.reactions).length) h += m.jumbo && Object.keys(m.jumbo).length ? 48 : 32; // a jumbo counter is taller (#714)
  if (m.thread) h += 34;
  if (m.state === "failed") h += 18;
  if (m.card && !m.deleted) h += cardHeight(m.card, undefined, { narrow: narrowCards() }) - 20;
  if (it.badges) h += 24;
  if (m.system && !m.deleted) return L.stampPx(it) + (it.unread ? 21 : 0) + 26;
  return Math.max(22, h);
}

/** What the rows need that doesn't change from one message to the next. Stable between renders (rows are memoised). */
interface RowCtx {
  /** What the NEW line says (an away seam changes it, #403). */
  unreadLabel: string;
  names: Map<string, string>;
  me: string | null;
  channels?: ChannelRefs;
  touch: boolean;
  threads: boolean;
  plain: (t: string) => string;
  /** The latest handlers (they change every render; rows reach them through this so they needn't re-render). */
  act: React.RefObject<Actions>;
  /** Half-typed edits, kept if the row scrolls out of the drawn window and back. */
  drafts: Map<string, string>;
  /** Watches a row's size (images, previews and fonts can change it after it's drawn). */
  observe: (el: HTMLElement | null) => void;
  cards?: CardHost;
  bloom?: BloomRows;
  lists?: ListHost;
  links?: MessageLinkHost;
  /** The layout's rows (MESSAGE_LAYOUTS). */
  layout: MessageLayout;
  /** Settings → Appearance → Message display: compact. */
  compact: boolean;
  /** A 1:1 DM: no sender name above the bubbles. */
  oneToOne: boolean;
}

/** Messages that start a blooming thread show what's growing there instead of the plain "N replies" line. */
export interface BloomRows {
  /** Changes whenever what render() draws changes (rows re-render then). */
  key: string;
  render: (m: ChatMessage) => React.ReactNode | null;
}

interface Actions {
  retry: (id: string) => void;
  discard: (id: string) => void;
  toggle: (m: ChatMessage, emoji: string) => void;
  reply: (m: ChatMessage) => void;
  openThread?: (m: ChatMessage) => void;
  openForward?: (f: Forward) => void;
  startEdit: (m: ChatMessage) => void;
  endEdit: (m: ChatMessage, body: string | null) => void;
  remove: (m: ChatMessage, confirm: boolean) => void;
  suppressEmbeds?: (m: ChatMessage) => void;
  removeAttachment?: (m: ChatMessage, ref: string) => void;
  setAttachmentPrivate?: (m: ChatMessage, ref: string, on: boolean) => void;
  /** Opens the reaction picker for a message, next to the button that asked (null: no button, e.g. the phone menu). */
  picker: (id: string, anchor: DOMRect | null) => void;
  /** `original`: what "Open original" opens when the picture shown is a smaller copy; `as`: GIFs (animation, size) or a video clip. */
  lightbox: (src: string, original?: string, as?: OpenAs) => void;
  menu: (m: ChatMessage) => void;
  markUnread?: (m: ChatMessage) => void;
  jumpTo: (id: string) => void;
  /** The pointer (or keyboard focus) arrived on a reaction, or left it: its tooltip. */
  tipEnter: (el: HTMLElement, messageId: string, emoji: string, now?: boolean) => void;
  tipLeave: () => void;
  /** The message under the mouse (P pins it, L opens the list picker). */
  hover: (m: ChatMessage | null) => void;
  listPicker: (m: ChatMessage, anchor: DOMRect | null) => void;
  /** Opens (or closes) the hover bar's More menu by its button; with `at`, a right-click's, at the pointer. */
  more: (m: ChatMessage, button: HTMLElement, keyboard: boolean, at?: { x: number; y: number }) => void;
  /** Phones: a reaction was pressed and held: who reacted, opened on that emoji. */
  reactors: (m: ChatMessage, emoji: string) => void;
  /** The mouse held on a counter: grows, and jumbo on release (#714). */
  hold: ChipHold;
  /** A quick reaction held (#726): the hover bar's emoji, the More menu's and the phone sheet's. */
  quick: QuickHold;
  /** A quick reaction held to super or jumbo: sends it as the picker would. */
  heldReact: (m: ChatMessage, emoji: string, how: QuickHeld) => void;
}

/**
 * Press and hold on a reaction (phones): who reacted. The touch stops at the reaction, so the message's own
 * long-press (its sheet) never starts; a tap still reaches onClick and toggles. The lift after a hold that fired is
 * cancelled by longPress, so the hold doesn't also toggle. Swipes and scrolls still see the touch (their listeners are
 * native ones on the chat, below React's root), and any movement cancels the hold.
 */
function reactionPress(onFire: () => void) {
  const h = longPress(onFire);
  const stop =
    <E extends React.SyntheticEvent>(fn: (e: E) => void) =>
    (e: E) => {
      e.stopPropagation();
      fn(e);
    };
  return {
    onTouchStart: stop(h.onTouchStart),
    onTouchMove: stop(h.onTouchMove),
    onTouchEnd: stop(h.onTouchEnd),
    onTouchCancel: stop(h.onTouchCancel),
    onContextMenu: stop(h.onContextMenu),
  };
}

/**
 * The reaction key a message already has for this emoji, if any: the same key, or (a server emoji) a token for the
 * same id under an older name.
 */
export function reactionKey(m: ChatMessage, emoji: string): string | undefined {
  if (m.reactions?.[emoji]) return emoji;
  const id = customEmojiId(emoji);
  return id ? Object.keys(m.reactions ?? {}).find((k) => customEmojiId(k) === id) : undefined;
}

/** I've reacted to this message with this emoji (a renamed server emoji included). */
export function reactedWith(m: ChatMessage, emoji: string, me: string | null): boolean {
  const k = reactionKey(m, emoji);
  return !!me && !!k && !!m.reactions?.[k]?.includes(me);
}

/** The hover bar's emoji: the 3 used last on this device, server emoji included (re-drawn as soon as another is used). */
function QuickReacts({ m, me, act }: { m: ChatMessage; me: string | null; act: React.RefObject<Actions> }) {
  const recent = useUsedHere("recent", 3);
  const { byId } = useServerEmoji();
  return (
    <>
      {recent.map((q) => {
        const on = reactedWith(m, q, me);
        const label = `React ${reactionLabel(q, byId)}`;
        return (
          <button
            key={q}
            type="button"
            className={`act emoji${on ? " on" : ""}`}
            {...act.current.quick.bind(q, (how) => act.current.heldReact(m, q, how))}
            onClick={() => !act.current.quick.swallowClick() && act.current.toggle(m, q)}
            title={label}
            aria-label={label}
            aria-pressed={on}
          >
            <ReactionGlyph emoji={q} />
          </button>
        );
      })}
    </>
  );
}

const Row = memo(function Row({ it, ctx, editing, pressed, menuOpen, deletable, replyGone }: { it: Item; ctx: RowCtx; editing: boolean; pressed: boolean; menuOpen: boolean; deletable: boolean; replyGone: boolean }) {
  const { m, first } = it;
  const { names, me, act } = ctx;
  const L = ctx.layout;
  const name = names.get(m.authorId) ?? "Someone";
  const mine = !!me && m.authorId === me;
  const reactions = Object.entries(m.reactions ?? {});
  const effects = useReactionEffectsOn(m.id);
  // Jumbo reactions (#714): a landing drops the emoji onto its counter and jolts the message; its thud sounds at the impact.
  const landings = useJumboLandings(m.id);
  useEffect(() => {
    for (const [emoji, serial] of landings) thudForLanding(m.id, emoji, serial);
  }, [landings, m.id]);
  const jolt = landings.size ? ` jolt-${Math.max(...landings.values()) % 2 ? "a" : "b"}` : "";
  // @Bloom's replies: its logo and an "AI" tag, light markdown, and a shimmer (then a caret) while it writes.
  const bloom = m.authorId === BLOOM_ID;
  const busy = !!m.state || !!m.streaming;
  const lh = ctx.lists;
  /** The text the layout shows for the stored body (the IRC layout drops "/me "). */
  const shown = L.bodyText ? L.bodyText(m.body) : m.body;
  if (m.system && !m.deleted) {
    return (
      <div className="msg-wrap" data-row={m.id} data-h={it.hkey} ref={ctx.observe}>
        <L.Stamp it={it} />
        {it.unread && (
          <div className="new-divider" role="separator" aria-label={ctx.unreadLabel === "NEW" ? "New messages" : ctx.unreadLabel}>
            <span className="new-divider-label">{ctx.unreadLabel}</span>
          </div>
        )}
        <div className="msg system" data-mid={m.id}>
          {(() => {
            const line = <SystemLine m={m} who={name} lists={lh?.byId ?? new Map()} onOpenList={(id) => lh?.open(id)} onJump={(id) => act.current.jumpTo(id)} />;
            return L.System ? <L.System it={it}>{line}</L.System> : line;
          })()}
        </div>
      </div>
    );
  }
  const inLists = lh && !m.deleted ? lh.listsOf(m) : [];
  const pinned = !!lh?.pins && inLists.includes(lh.pins.id);
  return (
    <div className="msg-wrap" data-row={m.id} data-h={it.hkey} ref={ctx.observe}>
      <L.Stamp it={it} />
      {it.unread && (
        <div className="new-divider" role="separator" aria-label={ctx.unreadLabel === "NEW" ? "New messages" : ctx.unreadLabel}>
          <span className="new-divider-label">{ctx.unreadLabel}</span>
        </div>
      )}
      <div
        className={`msg${first ? " first" : ""}${m.state ? ` ${m.state}` : ""}${bloom ? " bloom" : ""}${m.streaming ? " streaming" : ""}${pressed ? " pressed" : ""}${menuOpen ? " menu-open" : ""}${jolt}${m.authorId !== me && mentionsUser(m.body, me) ? " mentioned" : ""}${L.rowClass(it, mine, editing)}`}
        data-mid={m.id}
        onMouseEnter={ctx.touch ? undefined : () => act.current.hover(m)}
        onMouseLeave={ctx.touch ? undefined : () => act.current.hover(null)}
        onContextMenu={ctx.touch || busy || m.deleted || editing ? undefined : (e) => {
          // Right-click (desktop): the full message menu at the pointer, as a long press on a phone (#143). A link, a
          // picture, a video or selected text keeps the browser's own menu (Copy link, Save image, Copy).
          if ((e.target as HTMLElement).closest("a, img, video, audio, input, textarea, [contenteditable='true']")) return;
          const sel = window.getSelection();
          if (sel && !sel.isCollapsed && sel.anchorNode && e.currentTarget.contains(sel.anchorNode)) return;
          e.preventDefault();
          act.current.more(m, e.currentTarget, false, { x: e.clientX, y: e.clientY });
        }}
        onClick={ctx.touch || busy || editing ? undefined : (e) => {
          // Alt+click (desktop): mark unread from here, as in Discord. Not on links or controls.
          if (!e.altKey || !act.current.markUnread || (e.target as HTMLElement).closest("a, button, input, textarea, video, audio")) return;
          e.preventDefault();
          act.current.markUnread(m);
        }}
        {...(ctx.touch && !busy && !m.deleted && !editing ? { ...longPress(() => act.current.menu(m)), onTouchStartCapture: () => touchLink(null) } : {})}
      >
        <L.Frame
          it={it}
          name={name}
          mine={mine}
          quiet={busy || !!m.deleted || editing}
          compact={ctx.compact}
          oneToOne={ctx.oneToOne}
          tags={
            <>
              {bloom && <span className="ai-tag" title="Bloom is an AI">AI</span>}
              {isBotId(m.authorId) && <span className="ai-tag bot-tag" title="A bot made in this server">BOT</span>}
              {m.via && <span className="ai-tag via-tag" title={`Sent by ${name}'s AI, ${m.via}, for them`}>via {m.via}</span>}
            </>
          }
          replyRef={
            m.replyTo ? (
              <button type="button" className="reply-ref" onClick={() => act.current.jumpTo(m.replyTo!.id)}>
                <span className="reply-spine" aria-hidden="true" />
                <Avatar id={m.replyTo.authorId} name={names.get(m.replyTo.authorId) ?? "?"} size={16} />
                <strong>{names.get(m.replyTo.authorId) ?? "Someone"}</strong>
                <span className="reply-excerpt">
                  {replyGone ? <em>Original message was deleted</em> : ctx.plain(markdownToPlain(m.replyTo.excerpt, { spoiler: SPOILER_MASK, time: formatStamp })) || (m.replyTo.attachments ? "Click to see attachment" : "")}
                </span>
              </button>
            ) : null
          }
        >
          {m.deleted && <div className="body muted"><em>Message deleted</em></div>}
          {m.forward && !m.deleted && <ForwardBlock m={m} f={m.forward} ctx={ctx} />}
          {m.card && !m.deleted && ctx.cards && !bloom ? (
            <MessageCard card={m.card} host={ctx.cards} onOpenMedia={(src, original, video) => act.current.lightbox(src, original, video ? { clip: true } : undefined)} />
          ) : editing ? (
            <InlineEditor
              initial={ctx.drafts.get(m.id) ?? ctx.plain(m.body)}
              onChange={(t) => ctx.drafts.set(m.id, t)}
              onDone={(body) => {
                ctx.drafts.delete(m.id);
                act.current.endEdit(m, body);
              }}
            />
          ) : bloom && !m.deleted ? (
            m.streaming && !m.body ? (
              <L.Bubble>
                <div className="body bloom-thinking" role="status">
                  <span className="shimmer">Bloom is thinking…</span>
                </div>
              </L.Bubble>
            ) : (
              <>
                <L.Bubble>
                  <div className="body">
                    <Markdown source={m.body} open={m.streaming} text={(t) => <Body text={t} names={names} me={me} channels={ctx.channels} />} />
                    {m.streaming && <span className="stream-caret" aria-hidden="true" />}
                  </div>
                </L.Bubble>
                {/* A card from an app tool Bloom used, under its words. */}
                {m.card && ctx.cards && <MessageCard card={m.card} host={ctx.cards} onOpenMedia={(src, original, video) => act.current.lightbox(src, original, video ? { clip: true } : undefined)} />}
              </>
            )
          ) : (
            m.body && (
              <L.Bubble bare={onlyEmoji(m.body)}>
                <div className={`body${onlyEmoji(m.body) ? " jumbo" : ""}`}>
                  {hasMarkdown(shown) ? (
                    <Markdown source={shown} text={(t) => <Body text={t} names={names} me={me} channels={ctx.channels} />} />
                  ) : (
                    <Body text={shown} names={names} me={me} channels={ctx.channels} />
                  )}
                  {m.editedAt && <span className="edited" title={new Date(m.editedAt).toLocaleString()}> (edited)</span>}
                </div>
              </L.Bubble>
            )
          )}
          {m.attachments && (L.Attachments ? (
            <L.Attachments
              messageId={m.id}
              attachments={m.attachments}
              onOpen={(src, original, as) => act.current.lightbox(src, original, as)}
              onRemove={deletable && act.current.removeAttachment ? (ref) => act.current.removeAttachment!(m, ref) : undefined}
            />
          ) : (
            <Attachments
              messageId={m.id}
              attachments={m.attachments}
              onOpen={(src, original, as) => act.current.lightbox(src, original, as)}
              onRemove={deletable && act.current.removeAttachment ? (ref) => act.current.removeAttachment!(m, ref) : undefined}
              onSetPrivate={!m.state && m.authorId === me && act.current.setAttachmentPrivate ? (ref, on) => act.current.setAttachmentPrivate!(m, ref, on) : undefined}
            />
          ))}
          {m.embeds && m.embeds.length > 0 && !editing && (
            <Embeds
              messageId={m.id}
              embeds={m.embeds}
              onOpen={(src, original, as) => act.current.lightbox(src, original, as)}
              onSuppress={deletable && act.current.suppressEmbeds ? () => act.current.suppressEmbeds!(m) : undefined}
            />
          )}
          {ctx.links && !editing && !m.deleted && m.body && linkPreviewsIn(m.body, ctx.links, m.id).map((r) => <LinkPreview key={r.messageId} link={r} ctx={ctx} />)}
          {reactions.length > 0 && (
            <div className="reactions">
              {reactions.map(([emoji, users]) => {
                const mine = !!me && users.includes(me);
                const who = users.map((u) => names.get(u) ?? "Someone").join(", ");
                const label = reactionLabel(emoji, NO_EMOJI);
                // A counter is jumbo while at least one reaction on it is (#714): it draws its emoji twice the size.
                const big = !!m.jumbo?.[emoji]?.length;
                const land = landings.get(emoji);
                // A super reaction plays once (#87); a new serial remounts the chip, which restarts its animation.
                const fx = effects.get(emoji);
                return (
                  <button
                    key={fx ? `${emoji} ${fx.serial}` : emoji}
                    type="button"
                    className={`reaction${mine ? " mine" : ""}${big ? " jumbo-reaction" : ""}${fx ? ` fx-${fx.effect}` : ""}`}
                    {...(ctx.touch ? reactionPress(() => act.current.reactors(m, emoji)) : {})}
                    onPointerDown={(e) => act.current.hold.down(e, m.id, emoji)}
                    onPointerUp={() => act.current.hold.up()}
                    onClick={() => !act.current.hold.swallowClick() && act.current.toggle(m, emoji)}
                    onPointerEnter={(e) => e.pointerType === "mouse" && act.current.tipEnter(e.currentTarget, m.id, emoji)}
                    onPointerLeave={() => {
                      act.current.hold.leave();
                      act.current.tipLeave();
                    }}
                    onPointerCancel={() => act.current.hold.cancel()}
                    onFocus={(e) => e.currentTarget.matches(":focus-visible") && act.current.tipEnter(e.currentTarget, m.id, emoji, true)}
                    onBlur={() => act.current.tipLeave()}
                    aria-pressed={mine}
                    aria-label={`${big ? "Jumbo " : ""}${label} ${users.length}, ${who}`}
                  >
                    <span key={land} className={`reaction-emoji${land ? " jumbo-land" : ""}`}><ReactionGlyph emoji={emoji} /></span>
                    <span>{users.length}</span>
                    {fx?.effect === "poof" && (
                      <span className="reaction-puffs" aria-hidden="true">
                        {[1, 2, 3, 4, 5, 6].map((i) => <span key={i} className={`puff p${i}`} />)}
                      </span>
                    )}
                  </button>
                );
              })}
              <button type="button" className="reaction add" onClick={(e) => act.current.picker(m.id, e.currentTarget.getBoundingClientRect())} aria-label="Add reaction">
                <SmileIcon />
              </button>
            </div>
          )}
          {lh && inLists.length > 0 && <ListBadges ids={inLists} lists={lh.byId} onOpen={lh.open} />}
          {ctx.bloom?.render(m)}
          {m.thread && ctx.threads && !ctx.bloom?.render(m) && (
            <button type="button" className="thread-link" onClick={() => act.current.openThread?.(m)}>
              <span className="thread-avatars">
                {m.thread.participants.map((p) => <Avatar key={p} id={p} name={names.get(p) ?? "?"} size={18} />)}
              </span>
              <strong>{m.thread.count} {m.thread.count === 1 ? "reply" : "replies"}</strong>
              <span className="muted">Last reply {ago(m.thread.lastTs)}</span>
            </button>
          )}
          {m.state === "failed" && (
            <div style={{ fontSize: 12 }}>
              <span className="error">{m.error ?? "Not sent."}</span> <button className="link-btn" onClick={() => act.current.retry(m.id)}>Retry</button>
              {" · "}
              <button className="link-btn" onClick={() => act.current.discard(m.id)}>Delete</button>
            </div>
          )}
        </L.Frame>
        {!busy && !m.deleted && !editing && (
          <div className="msg-actions" role="toolbar" aria-label="Message actions">
            <QuickReacts m={m} me={me} act={act} />
            <button type="button" className="act" onClick={(e) => act.current.picker(m.id, e.currentTarget.getBoundingClientRect())} title="Add reaction" aria-label="Add reaction">
              <SmileIcon />
            </button>
            <span className="act-sep" aria-hidden="true" />
            <button type="button" className="act" onClick={() => act.current.reply(m)} title="Reply" aria-label="Reply">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{MENU_ICONS.reply}</svg>
            </button>
            {ctx.threads && (
              <button type="button" className="act" onClick={() => act.current.openThread?.(m)} title={m.thread ? "Open thread" : "Reply in thread"} aria-label={m.thread ? "Open thread" : "Reply in thread"}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{MENU_ICONS.thread}</svg>
              </button>
            )}
            <button
              type="button"
              className={`act${menuOpen ? " on" : ""}`}
              onClick={(e) => act.current.more(m, e.currentTarget, e.detail === 0)}
              title="More"
              aria-label="More"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <MoreIcon />
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

/**
 * The message list draws only the rows in view plus a margin (OVERSCAN_PX) above and below; spacers stand in for
 * the rest, sized from measured heights where we have them and estimates where we don't.
 *
 * Keeping the view steady: while you're at the bottom it stays pinned there. Otherwise we remember an anchor, the
 * first row in view and how far from the top it sits, and after every change (older messages loaded above, a row
 * above measured taller than guessed, an image settling) we put that row back where it was. Opening a channel on
 * its NEW line, jumping to a quoted message and "Jump to present" are all just a new anchor (or pinning).
 */
export function MessageList({ channelId, messages: all, names, me, more, loadingOlder, onLoadOlder, onLoadBack, onRetry, onDiscard, onReact, onReply, onDelete, canDelete, onSuppressEmbeds, onRemoveAttachment, onSetAttachmentPrivate, editingId, onStartEdit, onEndEdit, onOpenThread, onMarkUnread, onForward, onOpenForward, empty, start = "This is the start of the channel.", header, channels, everyone = true, oneToOne = false, unreadAfter, unreadLabel, seamTick = 0, seenProbe, lastSeq, readSeq, onAtBottom, cards, footer, lists, links, jumpRequest, blooming }: Props) {
  // Deleted messages disappear, except a thread's parent, which stays as a placeholder so the thread is reachable.
  const messages = all.filter((m) => !m.deleted || m.thread);
  const deletedIds = new Set(all.filter((m) => m.deleted).map((m) => m.id));
  const [confirming, setConfirming] = useState<ChatMessage | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const topPad = useRef<HTMLDivElement>(null);
  const bottomPad = useRef<HTMLDivElement>(null);
  const [picker, setPicker] = useState<{ id: string; anchor: DOMRect | null } | null>(null);
  const [lightbox, setLightbox] = useState<{ src: string; original: string; as?: OpenAs } | null>(null);
  /** Phones: the message whose long-press menu is open. */
  const [menuFor, setMenuFor] = useState<ChatMessage | null>(null);
  /** Short links (#734): the link menu (right-click) and the link a phone's long press started on (rows in the sheet). */
  const [linkMenu, setLinkMenu] = useState<{ target: LinkTarget; at: { x: number; y: number } } | null>(null);
  const [sheetLink, setSheetLink] = useState<LinkTarget | null>(null);
  const shortHost = useMemo(() => ({ menu: (target: LinkTarget, at: { x: number; y: number }) => setLinkMenu({ target, at }) }), []);
  /** The list picker, for a message (by the button it came from; null on a phone). */
  const [listPick, setListPick] = useState<{ m: ChatMessage; anchor: DOMRect | null } | null>(null);
  // The hover bar's More menu: which message, and its button (focus goes back there on Escape).
  const [moreFor, setMoreFor] = useState<{ m: ChatMessage; button: HTMLElement; keyboard: boolean; at?: { x: number; y: number } } | null>(null);
  const hovered = useRef<ChatMessage | null>(null);
  /** The reaction tooltip: shown after TIP_DELAY_MS on one reaction, at once when moving on to the next. */
  const [tip, setTip] = useState<ReactionTip | null>(null);
  /** Phones: the who-reacted sheet, for a message and the emoji that was held. */
  const [reactorsFor, setReactorsFor] = useState<{ id: string; emoji: string } | null>(null);
  /** Bumped when the list scrolls under an open tooltip, so it follows its reaction. */
  const [tipMoved, setTipMoved] = useState(0);
  const tipTimer = useRef<number | undefined>(undefined);
  const tipHiddenAt = useRef(0);
  const tipEnter = (anchor: HTMLElement, messageId: string, emoji: string, now = false) => {
    clearTimeout(tipTimer.current);
    const next = { anchor, messageId, emoji };
    // Names like ":joy:" come from the emoji data: start loading it during the wait.
    if (!emojiReady()) void loadEmoji();
    // Already showing one, or one just went away (sliding along the row): no wait.
    if (now || tip || performance.now() - tipHiddenAt.current < 300) return setTip(next);
    tipTimer.current = window.setTimeout(() => {
      tipTimer.current = undefined;
      setTip(next);
    }, TIP_DELAY_MS);
  };
  const tipLeave = () => {
    clearTimeout(tipTimer.current);
    tipTimer.current = undefined;
    if (tip) tipHiddenAt.current = performance.now();
    setTip(null);
  };
  useEffect(() => () => clearTimeout(tipTimer.current), []);
  // A different conversation: no tooltip carried over.
  useEffect(() => {
    tipLeave();
    setReactorsFor(null);
  }, [channelId]);
  const [toast, setToast] = useState<string | null>(null);
  const touch = isTouch();
  const flash = (t: string) => {
    setToast(t);
    setTimeout(() => setToast(null), 1400);
  };
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  // The first unread message (for the "NEW" line), if we hold it: we must reach back to the read position.
  const confirmed = all.filter((m) => !m.state);
  const firstUnreadId =
    unreadAfter != null && confirmed.length && (confirmed[0]!.seq <= unreadAfter + 1 || !more)
      ? messages.find((m) => !m.state && m.seq > unreadAfter)?.id
      : undefined;
  /** Others' messages after my read position, for the jump pill. */
  const newCount = readSeq === undefined ? 0 : confirmed.filter((m) => m.seq > readSeq && !m.deleted && m.authorId !== me).length;
  const [atBottom, setAtBottom] = useState(true);
  const [away, setAway] = useState(false);

  // ---- What the rows look like ----
  const chatLayout = useChatLayout();
  const display = useMessageDisplay();
  const compact = display === "compact";
  const textSize = useTextSize();
  const layout = chatLayout === "irc" ? ircMessages : MESSAGE_LAYOUTS[display];
  const items: Item[] = messages.map((m, i) => {
    const prev = messages[i - 1];
    const newDay = !prev || dayKey(prev.ts) !== dayKey(m.ts);
    const unread = m.id === firstUnreadId;
    // A quiet line stands apart from messages: the message after it starts a new group.
    // So is one their AI sent (or the first they wrote after it): the "via" mark sits on the name line.
    // The shared rule (#656) adds the quiet gap: more than 7 minutes since the message above starts a group.
    const first = startsGroup({
      prevTs: prev ? prev.ts : null, ts: m.ts, sameAuthor: prev?.authorId === m.authorId, sameVia: prev?.via === m.via,
      newDay, reply: !!m.replyTo, newLine: unread, apart: prev?.state === "failed" || !!prev?.system,
    });
    const badges = lists && !m.deleted && !m.system ? lists.listsOf(m).filter((id) => lists.byId.has(id)).length : 0;
    const stamp = bubbleStamp(prev?.ts, m.ts);
    return { m, first, last: true, newDay, stamp, unread, badges, hkey: "" };
  });
  // A run ends where the next message starts one. The key holds everything that changes a row's height: the display
  // mode and text size too, since a row measured cozy (or at one text size) says nothing about another.
  for (let i = 0; i < items.length; i++) {
    const it = items[i]!;
    it.last = i === items.length - 1 || items[i + 1]!.first;
    it.hkey = `${it.m.id}:${layout.id[0]}${layout.key(it)}${+it.unread}${it.badges}${compact ? "c" : ""}${textSize[0]}`;
  }
  const firstUnreadIdx = firstUnreadId ? items.findIndex((it) => it.unread) : -1;

  // ---- Windowing state (refs: none of it should cause a render by itself) ----
  /** Measured row heights by Item.hkey. */
  const heights = useRef(new Map<string, number>());
  /** The scroll box as of the last layout or scroll: list top (below the header), size and scroll position. */
  const view = useRef({ listTop: 0, height: 0, width: 0, scrollTop: 0 });
  const pinned = useRef(true);
  /** "Jump to present" is smooth-scrolling down: rows measured on the way mustn't pull the view back. */
  const headingDown = useRef(false);
  /**
   * The row to hold still, and where: `offset` is the distance from the top of the view to the row's top edge (to
   * place something, like the NEW line) or its bottom edge (when remembering where you are: a row that loses its
   * day line or name when older messages join its group above shrinks at the top, and its text shouldn't move).
   */
  const anchor = useRef<{ id: string; offset: number; edge: "top" | "bottom" } | null>(null);
  /** The channel whose opening position has been set (once it had messages). */
  const positioned = useRef<string | null>(null);
  /** A message to bring into view (a quote's original, or the one being edited). */
  const jump = useRef<{ id: string; flash: boolean } | null>(null);
  /** A jump asked for before this list's messages arrived: made once they do. */
  const waitingJump = useRef<{ id: string; highlight: boolean } | null>(null);
  /**
   * Opening the channel is loading back to its first unread: land on the NEW line when it arrives. Meanwhile the
   * list sits at the bottom without counting as read. (A token, so a late finish can't clear a newer wait.)
   */
  const awaitingNew = useRef<object | null>(null);
  /** The person has scrolled since the channel opened (then a late NEW line doesn't move them). */
  const userMoved = useRef(false);
  /** The last `seamTick` this list acted on (#403). */
  const seamSeen = useRef(seamTick);
  const drawn = useRef({ start: 0, end: 0, count: 0 });
  /**
   * Guesses vs. what rows measured, to scale later guesses (fonts and widths differ by device): sums so far.
   */
  const calib = useRef({ guessed: 0, measured: 0 });
  /**
   * Extra blank space above the rows. When the rows above the view turn out shorter than guessed while you're at
   * the very top, holding your place would need a negative scroll position; this takes up the difference instead,
   * and is quietly removed once it's out of sight (or older messages fill it).
   */
  const filler = useRef(0);
  const passes = useRef(0);
  const shownChannel = useRef<string | null>(null);
  const drafts = useRef(new Map<string, string>());
  if (shownChannel.current !== channelId) {
    // A different channel: start over at the bottom (its opening position is worked out below).
    shownChannel.current = channelId;
    filler.current = 0;
    pinned.current = true;
    headingDown.current = false;
    anchor.current = null;
    positioned.current = null;
    jump.current = null;
    waitingJump.current = null;
    awaitingNew.current = null;
    userMoved.current = false;
    seamSeen.current = seamTick;
  }

  // ---- Which rows to draw ----
  const width = view.current.width || 800;
  const scale = calib.current.guessed > 2000 ? Math.min(2, Math.max(0.5, calib.current.measured / calib.current.guessed)) : 1;
  const offs = new Array<number>(items.length + 1);
  offs[0] = 0;
  for (let i = 0; i < items.length; i++) offs[i + 1] = offs[i]! + (heights.current.get(items[i]!.hkey) ?? estimate(items[i]!, width, layout, compact) * scale);
  const total = offs[items.length]!;
  const vh = view.current.height || 900;
  const anchorIdx = anchor.current ? items.findIndex((it) => it.m.id === anchor.current!.id) : -1;
  // The top of the view, measured from the first message.
  const viewTop = pinned.current
    ? total - vh
    : anchorIdx >= 0
      ? offs[anchor.current!.edge === "top" ? anchorIdx : anchorIdx + 1]! - anchor.current!.offset
      : view.current.scrollTop - view.current.listTop - filler.current;
  let from = 0;
  while (from < items.length && offs[from + 1]! < viewTop - OVERSCAN_PX) from++;
  let to = from;
  while (to < items.length && offs[to]! < viewTop + vh + OVERSCAN_PX) to++;
  drawn.current = { start: from, end: to, count: items.length };

  const rowEl = (id: string) => box.current?.querySelector<HTMLElement>(`[data-row="${CSS.escape(id)}"]`) ?? null;

  /** Remembers the first row in view and where it sits, so it can be put back after the rows above change. */
  const saveAnchor = () => {
    const el = box.current;
    if (!el) return;
    for (const row of el.querySelectorAll<HTMLElement>("[data-row]")) {
      if (row.offsetTop + row.offsetHeight > el.scrollTop) {
        anchor.current = { id: row.dataset.row!, offset: row.offsetTop + row.offsetHeight - el.scrollTop, edge: "bottom" };
        return;
      }
    }
  };

  /** Pinned: to the bottom. Otherwise: the anchor row back where it was. */
  const placeView = () => {
    const el = box.current;
    if (!el) return;
    // On the way down to the present: holding the old place would stop the scroll short.
    if (headingDown.current && !pinned.current) return;
    if (pinned.current) {
      el.scrollTop = el.scrollHeight;
      return;
    }
    const a = anchor.current;
    const row = a && rowEl(a.id);
    if (!a || !row) return;
    const want = row.offsetTop + (a.edge === "bottom" ? row.offsetHeight : 0) - a.offset;
    if (want < 0 && topPad.current) {
      // Can't scroll above the top: make room instead (see `filler`).
      filler.current += -want;
      topPad.current.style.height = `${topPad.current.offsetHeight - want}px`;
      el.scrollTop = 0;
      return;
    }
    if (Math.abs(el.scrollTop - want) >= 1) el.scrollTop = want;
  };

  /** Takes the filler away once it's well out of sight, scrolling by the same amount so nothing visibly moves. */
  const dropFiller = () => {
    const el = box.current;
    const tp = topPad.current;
    const f = filler.current;
    // Everything from the spacer down moves up by f and the view follows, so only the top (header, "Load older")
    // could visibly move: wait until it's out of view even after the scroll.
    if (!el || !tp || !f || el.scrollTop - f < tp.offsetTop) return;
    filler.current = 0;
    tp.style.height = `${tp.offsetHeight - f}px`;
    el.scrollTop -= f;
  };
  const placeRef = useRef(placeView);
  placeRef.current = placeView;

  /** Are the drawn rows still enough for where the view is? (If not, draw again around it.) */
  const covered = () => {
    const el = box.current;
    const tp = topPad.current;
    const bp = bottomPad.current;
    if (!el || !tp || !bp) return true;
    const top = el.scrollTop - view.current.listTop;
    const bottom = top + el.clientHeight;
    const { start, end, count } = drawn.current;
    return (start === 0 || tp.offsetHeight <= top - OVERSCAN_PX / 3) && (end >= count || bp.offsetTop - view.current.listTop >= bottom + OVERSCAN_PX / 3);
  };

  // Rows report size changes after they're drawn (a preview arriving, fonts loading, the window narrowing).
  const observer = useRef<ResizeObserver | null>(null);
  observer.current ??=
    typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver((entries) => {
          let changed = false;
          for (const e of entries) {
            const row = e.target as HTMLElement;
            if (!row.isConnected) continue;
            const key = row.dataset.h!;
            const h = row.offsetHeight;
            if (heights.current.get(key) !== h) {
              heights.current.set(key, h);
              changed = true;
            }
          }
          if (!changed) return;
          placeRef.current();
          rerender();
        });
  useEffect(() => () => observer.current?.disconnect(), []);
  const observe = useCallback((el: HTMLElement | null) => {
    if (!el) return;
    observer.current?.observe(el);
    return () => observer.current?.unobserve(el);
  }, []);

  // The list itself changes height (the phone keyboard opening, the composer growing): keep what's at the bottom of
  // the view where it is, so the conversation moves up with the keyboard instead of being covered by it.
  const boxObserver = useRef<ResizeObserver | null>(null);
  const boxHeight = useRef(0);
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    boxHeight.current = el.clientHeight;
    boxObserver.current = new ResizeObserver(() => {
      const h = el.clientHeight;
      const delta = boxHeight.current - h;
      boxHeight.current = h;
      if (!delta) return;
      view.current.height = h;
      if (pinned.current) {
        el.scrollTop = el.scrollHeight;
        // The browser can still put the old position back right after (seen on Android-sized screens while the
        // composer grows line by line), leaving the newest lines behind the box: settle on the bottom again shortly.
        for (const ms of [0, 80]) {
          setTimeout(() => {
            if (pinned.current) el.scrollTop = el.scrollHeight;
          }, ms);
        }
      } else if (!headingDown.current) {
        el.scrollTop += delta;
        saveAnchor();
      }
      rerender();
    });
    boxObserver.current.observe(el);
    return () => boxObserver.current?.disconnect();
  }, []);

  // The footer (private cards) grows and shrinks outside the rows: stay pinned to the bottom when we were there.
  const footerObserver = useRef<ResizeObserver | null>(null);
  const observeFooter = useCallback((el: HTMLElement | null) => {
    footerObserver.current?.disconnect();
    if (!el || typeof ResizeObserver === "undefined") return;
    footerObserver.current = new ResizeObserver(() => {
      if (pinned.current) placeRef.current();
    });
    footerObserver.current.observe(el);
  }, []);
  useEffect(() => () => footerObserver.current?.disconnect(), []);

  /** Works out whether we're at the bottom, and tells the page when that changes. */
  const sync = () => {
    const el = box.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    pinned.current = dist < BOTTOM_PX;
    setAtBottom(pinned.current);
    setAway(dist > AWAY_PX);
    if (positioned.current === channelId && !awaitingNew.current) onAtBottom?.(channelId, pinned.current, confirmed.at(-1)?.seq ?? 0);
  };

  /** Opens on the NEW line if it would be off screen at the bottom. False if we don't hold the first unread. */
  const placeOnNewLine = () => {
    if (firstUnreadIdx < 0) return false;
    const lineTop = offs[firstUnreadIdx]! + layout.stampPx(items[firstUnreadIdx]!);
    if (total - lineTop > vh - NEW_LINE_TOP) {
      pinned.current = false;
      anchor.current = { id: items[firstUnreadIdx]!.m.id, offset: NEW_LINE_TOP - layout.stampPx(items[firstUnreadIdx]!), edge: "top" };
    }
    return true;
  };

  /**
   * An away seam (#403) was just set: bring its line into view if it is off screen, as opening on the NEW line does.
   * At the bottom, the line is already in view when the new messages are few; scrolled up, it is when it sits in the
   * view. False if we don't hold its message.
   */
  const placeOnSeam = () => {
    if (firstUnreadIdx < 0) return false;
    const lineTop = offs[firstUnreadIdx]! + layout.stampPx(items[firstUnreadIdx]!);
    if (lineTop >= viewTop && lineTop <= viewTop + vh - NEW_LINE_TOP / 2) return false;
    pinned.current = false;
    anchor.current = { id: items[firstUnreadIdx]!.m.id, offset: NEW_LINE_TOP - layout.stampPx(items[firstUnreadIdx]!), edge: "top" };
    return true;
  };

  /** The newest message seen right now (#403): the end of the list when at the bottom, else the last row in view. */
  const seenNow = () => {
    const el = box.current;
    const newest = confirmed.at(-1)?.seq ?? 0;
    if (!el || pinned.current) return newest;
    const bottom = el.scrollTop + el.clientHeight;
    const seqOf = new Map(confirmed.map((m) => [m.id, m.seq]));
    let seen = 0;
    for (const row of el.querySelectorAll<HTMLElement>("[data-row]")) {
      if (row.offsetTop >= bottom) break;
      seen = Math.max(seen, seqOf.get(row.dataset.row!) ?? 0);
    }
    return seen;
  };
  if (seenProbe) seenProbe.current = seenNow;
  useEffect(() => () => {
    if (seenProbe) seenProbe.current = null;
  }, [seenProbe]);

  // After every render: measure what's drawn, settle where the view goes, then check the drawn rows cover it.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const v = view.current;
    if (Math.abs(el.clientWidth - v.width) > 1) {
      // Text wraps differently at a new width: old measurements no longer hold.
      if (v.width) heights.current.clear();
      v.width = el.clientWidth;
    }
    v.height = el.clientHeight;
    v.listTop = topPad.current?.offsetTop ?? 0;
    if (heights.current.size > 20_000) heights.current.clear();
    const c = calib.current;
    for (const row of el.querySelectorAll<HTMLElement>("[data-row]")) {
      const key = row.dataset.h!;
      if (!heights.current.has(key)) {
        // First measurement of this row: how good was the guess?
        const it = items.find((x) => x.hkey === key);
        if (it && !it.m.embeds?.length) {
          c.guessed += estimate(it, v.width, layout, compact);
          c.measured += row.offsetHeight;
        }
      }
      heights.current.set(key, row.offsetHeight);
    }
    let again = false;

    if (positioned.current !== channelId && items.length) {
      // A channel just opened: the bottom, or its NEW line if that would be off screen. Too many unread to hold?
      // Load back to the first one (unless it's far too many, or the channel was never opened) and land there.
      positioned.current = channelId;
      if (!placeOnNewLine() && unreadAfter != null && unreadAfter > 0 && more && onLoadBack && (lastSeq === undefined || lastSeq - unreadAfter <= MAX_UNREAD_JUMP)) {
        const wait = {};
        awaitingNew.current = wait;
        void onLoadBack({ seq: unreadAfter }).then(() =>
          // After React has drawn what the load brought (which places the line); if it couldn't get there, stop
          // waiting and carry on as usual at the bottom.
          setTimeout(() => {
            if (awaitingNew.current !== wait) return;
            awaitingNew.current = null;
            rerender();
          }, 0),
        );
      }
      again = !pinned.current;
    } else if (awaitingNew.current && firstUnreadIdx >= 0) {
      awaitingNew.current = null;
      if (!userMoved.current && placeOnNewLine()) again = !pinned.current;
    }

    // A new away seam (#403). Acted on once, whether or not its line is held; a jump asked for wins, as over the NEW line.
    if (seamTick !== seamSeen.current) {
      seamSeen.current = seamTick;
      if (seamTick > 0 && !jump.current && placeOnSeam()) again = true;
    }

    if (jump.current) {
      const j = jump.current;
      const row = rowEl(j.id);
      if (!items.some((it) => it.m.id === j.id)) jump.current = null;
      else if (row) {
        // Centred, and highlighted for a moment.
        pinned.current = false;
        el.scrollTop = row.offsetTop - Math.max(0, (el.clientHeight - row.offsetHeight) / 2);
        if (j.flash) {
          const msg = row.querySelector(".msg");
          msg?.classList.add("flash");
          // Whatever is flashing by then stops (the row may have been redrawn meanwhile).
          setTimeout(() => {
            msg?.classList.remove("flash");
            for (const f of box.current?.querySelectorAll(".msg.flash") ?? []) f.classList.remove("flash");
          }, 1200);
        }
        jump.current = null;
        saveAnchor();
      } else {
        // Not drawn yet: aim the window at it, draw, then centre it.
        pinned.current = false;
        anchor.current = { id: j.id, offset: el.clientHeight / 2 - 40, edge: "top" };
        again = true;
      }
    }

    // The anchor's message is gone (deleted): any row in view will do instead.
    if (anchor.current && !items.some((it) => it.m.id === anchor.current!.id)) {
      anchor.current = null;
      saveAnchor();
    }
    const a = anchor.current;
    if (pinned.current || !a || rowEl(a.id)) {
      placeView();
      if (!covered()) again = true;
      if (!pinned.current) saveAnchor();
      sync();
    } else again = true; // aiming at a row that isn't drawn yet: draw around it first
    dropFiller();
    v.scrollTop = el.scrollTop;
    // A few passes settle it (each draws closer to the truth); never loop forever.
    if (again && passes.current < 6) {
      passes.current++;
      rerender();
    } else passes.current = 0;
  });

  const onScroll = () => {
    const el = box.current;
    if (!el) return;
    // The tooltip follows its reaction (the list also scrolls by itself: pictures loading, keeping your place),
    // and goes once the reaction leaves the list's view.
    if (tip) {
      const a = tip.anchor.getBoundingClientRect();
      const b = el.getBoundingClientRect();
      if (a.bottom < b.top || a.top > b.bottom) tipLeave();
      else setTipMoved((n) => n + 1);
    }
    dropFiller();
    view.current.scrollTop = el.scrollTop;
    sync();
    if (!pinned.current) saveAnchor();
    if (!covered()) rerender();
    if (el.scrollTop < 200 && more && !loadingOlder) onLoadOlder();
  };
  const moved = () => {
    userMoved.current = true;
    headingDown.current = false;
  };

  const jumpToPresent = () => {
    const el = box.current;
    if (!el) return;
    // Smooth for a short way; a long way (pages of history) would take ages to animate, so just go.
    const far = el.scrollHeight - el.scrollTop - el.clientHeight > el.clientHeight * 3;
    anchor.current = null;
    if (far) {
      pinned.current = true;
      rerender();
      return;
    }
    // Rows below the view were drawn at guessed heights; once measured the bottom moves further down, so the
    // smooth scroll's target falls short. Land on the real bottom when it ends (or soon after, without scrollend).
    headingDown.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    let done = false;
    const land = () => {
      if (done) return;
      done = true;
      el.removeEventListener("scrollend", land);
      if (!headingDown.current) return; // the person took over
      headingDown.current = false;
      pinned.current = true;
      anchor.current = null;
      rerender();
    };
    el.addEventListener("scrollend", land);
    setTimeout(land, 900);
  };

  /** Brings a message into view: drawn, held but not drawn, or (a quote's original) loaded from the room first. */
  const jumpTo = async (id: string, highlight = true) => {
    if (!messages.some((m) => m.id === id)) {
      // Nothing here yet (a thread or channel just opened, its messages still on the way): try again once they come.
      if (!more && (loadingOlder || !messages.length)) {
        waitingJump.current = { id, highlight };
        return;
      }
      if (more && onLoadBack) await onLoadBack({ id });
    }
    // The jump wins over the channel's opening: a load back to the NEW line that finishes later mustn't move the view
    // off the message (search a recent message in a channel with many unread).
    awaitingNew.current = null;
    userMoved.current = true;
    jump.current = { id, flash: highlight };
    rerender();
  };
  useEffect(() => {
    const w = waitingJump.current;
    if (!w || loadingOlder || !messages.length) return;
    waitingJump.current = null;
    void jumpTo(w.id, w.highlight);
  }, [loadingOlder, messages.length, more]);

  // ↑ in the composer can start editing a message that's scrolled out of the drawn window: bring it in.
  useEffect(() => {
    if (editingId && !rowEl(editingId)) void jumpTo(editingId, false);
  }, [editingId]);

  /** Message text as typed: stored channel references shown as "#name", mentions as "@name". */
  const custom = useServerEmoji();
  /** The phone sheet's row: the 6 I use most here, server emoji included. */
  const sheetEmoji = useUsedHere("frequent", 6);
  const plain = (t: string) => decodeCustomEmoji(decodeMentions(channels ? decodeChannelRefs(t, channels.byId) : t, names), custom.byId);
  /** An edited text back to stored form: "@Name" → "<@id>", keeping the people the message already mentioned. */
  const encode = (m: ChatMessage, t: string) =>
    applyEmoticons(encodeCustomEmoji(encodeMentions(t, [...names].map(([id, name]) => ({ id, name })), { everyone, picked: mentionPicks(m.body, names) }), custom.emojis)); // :( → 🙁 on edit-save too (#630)

  const toggle = (m: ChatMessage, emoji: string) => {
    const on = !reactedWith(m, emoji, me);
    // Adding one counts as using it (the hover bar shows the last three), server emoji too.
    if (on) noteEmojiUse(emoji);
    // Taking one back uses the key the message has (a server emoji may have been renamed since).
    onReact(m.id, on ? emoji : (reactionKey(m, emoji) ?? emoji), on);
  };
  // Starting a reply opens the composer's "Replying to" bar, which shortens the list and slides the rows up under a still
  // mouse: the row below would light up as if it were the one replied to. No hover look until the mouse really moves.
  const [hoverStale, setHoverStale] = useState(false);
  const reply = (m: ChatMessage) => {
    setHoverStale(true);
    onReply(m);
  };
  // A counter held with the mouse sends the same emoji as my jumbo reaction (#714), under the key the message has.
  const chipHold = useChipHold((id, emoji) => {
    const m = messages.find((x) => x.id === id);
    onReact(id, (m && reactionKey(m, emoji)) ?? emoji, true, false, true);
  });
  // A quick reaction held sends what the picker's hold would (#726): super (a new reaction), or jumbo (also one I have).
  const quickHold = useQuickHold();
  const heldReact = (m: ChatMessage, emoji: string, how: QuickHeld) => {
    noteEmojiUse(emoji);
    if (how === "jumbo") onReact(m.id, reactionKey(m, emoji) ?? emoji, true, false, true);
    else if (!reactedWith(m, emoji, me)) onReact(m.id, emoji, true, true);
  };
  const act = useRef<Actions>(null!);
  act.current = {
    retry: onRetry,
    discard: onDiscard,
    toggle,
    quick: quickHold.hold,
    heldReact,
    reply,
    openThread: onOpenThread,
    openForward: onOpenForward,
    startEdit: onStartEdit,
    endEdit: (m, body) => onEndEdit(m, body === null || body.trim() === plain(m.body) ? null : encode(m, textToSend(body))),
    remove: (m, confirm) => (confirm ? setConfirming(m) : onDelete(m)),
    suppressEmbeds: onSuppressEmbeds,
    removeAttachment: onRemoveAttachment,
    setAttachmentPrivate: onSetAttachmentPrivate,
    picker: (id, anchor) => setPicker({ id, anchor }),
    lightbox: (src, original, as) => setLightbox({ src, original: original ?? src, as }),
    menu: (m) => {
      setSheetLink(takeTouchedLink());
      setMenuFor(m);
    },
    jumpTo: (id) => void jumpTo(id),
    markUnread: onMarkUnread ? (m) => (onMarkUnread(m), flash("Marked unread")) : undefined,
    hover: (m) => (hovered.current = m),
    tipEnter,
    tipLeave,
    reactors: (m, emoji) => setReactorsFor({ id: m.id, emoji }),
    hold: chipHold.hold,
    listPicker: (m, anchor) => setListPick({ m, anchor }),
    more: (m, button, keyboard, at) => setMoreFor((cur) => (!at && cur?.m.id === m.id ? null : { m, button, keyboard, at })),
  };
  // From a list's Jump: the page asks, we bring it in (loading back to it if needed).
  useEffect(() => {
    if (jumpRequest) void jumpTo(jumpRequest.id);
  }, [jumpRequest?.n]);
  // Desktop: P pins (or unpins) the message under the mouse, L opens its list picker.
  useEffect(() => {
    if (!lists || touch) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.isComposing) return;
      const k = e.key.toLowerCase();
      if (k !== "p" && k !== "l") return;
      const t = e.target as HTMLElement | null;
      if (t?.closest?.("input, textarea, select, [contenteditable='true'], .overlay, .list-picker, .emoji-picker, .sheet-backdrop, .msg-more")) return;
      const m = hovered.current;
      if (!m || m.state || m.deleted || m.system || m.streaming || !box.current?.querySelector(`[data-mid="${CSS.escape(m.id)}"]:hover`)) return;
      e.preventDefault();
      if (k === "p") {
        const on = !(lists.pins && lists.listsOf(m).includes(lists.pins.id));
        lists.toggle(m, PINS, on);
      } else {
        const bar = box.current?.querySelector<HTMLElement>(`[data-mid="${CSS.escape(m.id)}"] .msg-actions`);
        setListPick({ m, anchor: bar?.getBoundingClientRect() ?? null });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lists, touch]);
  // Rows only re-render when their message or this changes (channel names count: "#name" links are drawn by name).
  const channelsKey = channels ? [...channels.byId].join() : "";
  const ctx = useMemo<RowCtx>(
    () => ({ unreadLabel: unreadLabel ?? "NEW", names, me, channels, touch, threads: !!onOpenThread, plain, act, drafts: drafts.current, observe, cards, lists, links, bloom: blooming, layout, compact, oneToOne }),
    [unreadLabel, names, me, channelsKey, touch, !!onOpenThread, cards, custom.byId, lists?.version, lists?.pins?.id, lists?.inDm, links, blooming?.key, layout, compact, oneToOne],
  );

  // Message links in text read "Maya in #general" (MessageLinkChip).
  const linkText = useMemo<MessageLinkText | null>(
    () => (links ? { host: links, names, channelName: (id) => channels?.byId.get(id) } : null),
    [links, names, channelsKey],
  );

  const copyLink = (href: string) => void navigator.clipboard?.writeText(href).then(() => flash("Link copied"), () => flash("Couldn't copy"));
  return (
    <MessageLinkTextContext.Provider value={linkText}>
    <ShortLinkContext.Provider value={shortHost}>
    <div className={`messages${hoverStale ? " hover-stale" : ""}`} ref={box} onScroll={onScroll} onPointerMove={(e) => {
        // Only a real move (the browser's own moves after a layout change carry no movement).
        if (hoverStale && (e.movementX || e.movementY)) setHoverStale(false);
      }} onClick={(e) => {
        // A link to a message on Bloom opens here, not in a new tab (a modified click still opens a tab).
        if (!links || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        const a = (e.target as HTMLElement).closest?.("a[href]") as HTMLAnchorElement | null;
        const ref = a && parseMessageLink(a.href, location.origin);
        if (!ref) return;
        e.preventDefault();
        links.open(ref);
      }} onWheel={(e) => {
        moved();
        // Scrolling by hand closes the More menu (it'd be left beside nothing); the list scrolling itself doesn't.
        if (moreFor && !(e.target as HTMLElement).closest?.(".msg-more")) setMoreFor(null);
      }} onTouchStart={moved} onKeyDown={moved} role="log" aria-live="polite" aria-label="Messages">
      {header}
      {more ? (
        <div className="load-older">{loadingOlder ? "Loading older messages…" : <button className="link-btn" onClick={onLoadOlder}>Load older messages</button>}</div>
      ) : (
        messages.length > 0 && !header && <div className="load-older">{start}</div>
      )}
      {messages.length === 0 && !more && !loadingOlder && <div className="load-older" style={{ paddingTop: header ? 8 : 40 }}>{empty}</div>}
      <div ref={topPad} style={{ height: offs[from]! + filler.current }} aria-hidden="true" />
      {items.slice(from, to).map((it) => (
        <Row
          key={it.m.id}
          it={it}
          ctx={ctx}
          editing={editingId === it.m.id}
          pressed={menuFor?.id === it.m.id}
          menuOpen={moreFor?.m.id === it.m.id}
          deletable={!it.m.state && !it.m.deleted && canDelete(it.m)}
          replyGone={!!it.m.replyTo && deletedIds.has(it.m.replyTo.id)}
        />
      ))}
      <div ref={bottomPad} style={{ height: total - offs[to]! }} aria-hidden="true" />
      {footer && <div className="list-footer" ref={observeFooter}>{footer}</div>}
      {menuFor && (
        <Sheet label="Message actions" onClose={() => setMenuFor(null)}>
          <div className="msg-menu">
            <div className="msg-menu-quick" role="group" aria-label="React">
              {sheetEmoji.map((q) => {
                const mine = reactedWith(menuFor, q, me);
                return (
                  <button
                    key={q}
                    type="button"
                    className={`quick${mine ? " mine" : ""}`}
                    aria-pressed={mine}
                    aria-label={`React ${reactionLabel(q, custom.byId)}`}
                    {...quickHold.hold.bind(q, (how) => {
                      heldReact(menuFor, q, how);
                      setMenuFor(null);
                    })}
                    onClick={() => {
                      if (quickHold.hold.swallowClick()) return;
                      toggle(menuFor, q);
                      setMenuFor(null);
                    }}
                  >
                    <ReactionGlyph emoji={q} />
                  </button>
                );
              })}
              <button
                type="button"
                className="quick"
                aria-label="More reactions"
                onClick={() => {
                  setPicker({ id: menuFor.id, anchor: null });
                  setMenuFor(null);
                }}
              >
                <SmileIcon />
              </button>
            </div>
            {lists && !menuFor.system && (
              <div className="msg-menu-lists" role="group" aria-label="Pin or add to a collection">
                {(() => {
                  const held = lists.listsOf(menuFor);
                  const pinned = !!lists.pins && held.includes(lists.pins.id);
                  const quick = namedLists(lists.all, lists.serverId, lists.inDm, lists.actor, new Set(held)).slice(0, 3);
                  return (
                    <>
                      <button type="button" className={`list-badge${pinned ? " pins" : ""}`} aria-pressed={pinned} onClick={() => { lists.toggle(menuFor, PINS, !pinned); setMenuFor(null); }}>
                        <PinIcon size={14} /> <span>{pinned ? "Pinned" : "Pin"}</span>
                      </button>
                      {quick.map((l) => {
                        const on = held.includes(l.id);
                        return (
                          <button key={l.id} type="button" className={`list-badge${on ? " pins" : ""}`} aria-pressed={on} onClick={() => { noteListUse(lists.serverId, l.id); lists.toggle(menuFor, l.id, !on); setMenuFor(null); }}>
                            <ListGlyph list={l} size={14} /> <span>{l.name}</span>
                          </button>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            )}
            {sheetLink && (
              <div className="msg-menu-list" role="group" aria-label="Link">
                <MenuItem icon="open" label="Open link" onClick={() => { openLink(sheetLink.href); setMenuFor(null); }} />
                <MenuItem icon="copy" label="Copy link" onClick={() => { copyLink(sheetLink.href); setMenuFor(null); }} />
                <MenuItem icon="expand" label={showLabel(sheetLink)} onClick={() => { sheetLink.toggle(); setMenuFor(null); }} />
              </div>
            )}
            <div className="msg-menu-list">
              {(() => {
                const m = menuFor;
                const close = (f: () => void) => () => {
                  f();
                  setMenuFor(null);
                };
                // ACTION_GROUPS' order (#143); pins are the badges above.
                const items: Partial<Record<ActionKey, React.ReactNode>> = {
                  reply: <MenuItem icon="reply" label="Reply" onClick={close(() => reply(m))} />,
                  list: lists && !m.system && <MenuItem icon="list" label="Add to collection…" onClick={close(() => setListPick({ m, anchor: null }))} />,
                  thread: onOpenThread && <MenuItem icon="thread" label={m.thread ? "Open thread" : "Reply in thread"} onClick={close(() => onOpenThread(m))} />,
                  forward: onForward && !m.system && <MenuItem icon="forward" label="Forward" onClick={close(() => onForward(m))} />,
                  edit: m.authorId === me && !m.card && <MenuItem icon="edit" label="Edit message" onClick={close(() => onStartEdit(m))} />,
                  copy: m.body && <MenuItem icon="copy" label="Copy text" onClick={close(() => void navigator.clipboard?.writeText(plain(m.body)).then(() => flash("Copied"), () => flash("Couldn't copy")))} />,
                  download: !m.deleted && downloadables(m.attachments).length > 0 && <MenuItem icon="download" label={downloadLabel(m.attachments)} onClick={close(() => void download(downloadables(m.attachments)))} />,
                  link: links && !m.state && !m.system && <MenuItem icon="link" label="Copy message link" onClick={close(() => void navigator.clipboard?.writeText(messageLinkFor(links, m.id)).then(() => flash("Link copied"), () => flash("Couldn't copy")))} />,
                  unread: onMarkUnread && !m.state && <MenuItem icon="unread" label="Mark unread" onClick={close(() => { onMarkUnread(m); flash("Marked unread"); })} />,
                  delete: canDelete(m) && <MenuItem icon="delete" label="Delete message" danger onClick={close(() => setConfirming(m))} />,
                };
                return ACTION_GROUPS.flat().map((k) => items[k] && <Fragment key={k}>{items[k]}</Fragment>);
              })()}
            </div>
          </div>
        </Sheet>
      )}
      {toast && <div className="toast" role="status">{toast}</div>}
      {reactorsFor &&
        (() => {
          // Live: reactions can come and go while it's open (none left closes it).
          const m = all.find((x) => x.id === reactorsFor.id);
          return (
            <ReactionSheet
              key={reactorsFor.id + reactorsFor.emoji}
              reactions={(m && !m.deleted && m.reactions) || {}}
              jumbo={(m && !m.deleted && m.jumbo) || undefined}
              emoji={reactorsFor.emoji}
              names={names}
              me={me}
              onClose={() => setReactorsFor(null)}
            />
          );
        })()}
      {tip &&
        (() => {
          // Live: who reacted can change while it's open; a reaction that's gone takes its tooltip with it.
          const tipped = all.find((x) => x.id === tip.messageId);
          const users = tipped?.reactions?.[tip.emoji];
          return users?.length && tip.anchor.isConnected ? <ReactionTooltip tip={tip} moved={tipMoved} users={users} jumbo={tipped?.jumbo?.[tip.emoji]} names={names} me={me} bounds={() => box.current?.getBoundingClientRect() ?? null} /> : null;
        })()}
      {moreFor && (() => {
        const m = all.find((x) => x.id === moreFor.m.id);
        if (!m || m.deleted || !moreFor.button.isConnected) return null;
        const held = lists && !m.system ? lists.listsOf(m) : [];
        const pinned = !!lists?.pins && held.includes(lists.pins.id);
        const anchor = moreFor.button.getBoundingClientRect();
        return (
          <MoreMenu
            anchor={anchor}
            keyboard={moreFor.keyboard}
            button={moreFor.at ? undefined : moreFor.button}
            at={moreFor.at}
            mine={(q) => reactedWith(m, q, me)}
            onReact={(q) => toggle(m, q)}
            quick={quickHold.hold}
            onReactHeld={(q, how) => heldReact(m, q, how)}
            onAddReaction={(at) => setPicker({ id: m.id, anchor: at })}
            onReply={() => reply(m)}
            onThread={onOpenThread ? () => onOpenThread(m) : undefined}
            hasThread={!!m.thread}
            onForward={onForward && !m.system ? () => onForward(m) : undefined}
            onEdit={m.authorId === me && !m.card ? () => onStartEdit(m) : undefined}
            onCopy={m.body ? () => void navigator.clipboard?.writeText(plain(m.body)).then(() => flash("Copied"), () => flash("Couldn't copy")) : undefined}
            download={!m.deleted && downloadables(m.attachments).length ? { label: downloadLabel(m.attachments), run: () => void download(downloadables(m.attachments)) } : undefined}
            pin={lists && !m.system ? { pinned, toggle: () => lists.toggle(m, PINS, !pinned), addToList: (at) => setListPick({ m, anchor: at }) } : undefined}
            onCopyLink={links && !m.state && !m.system ? () => void navigator.clipboard?.writeText(messageLinkFor(links, m.id)).then(() => flash("Link copied"), () => flash("Couldn't copy")) : undefined}
            onMarkUnread={onMarkUnread && !m.state ? () => { onMarkUnread(m); flash("Marked unread"); } : undefined}
            onDelete={canDelete(m) ? (confirm) => (confirm ? setConfirming(m) : onDelete(m)) : undefined}
            onClose={(refocus) => {
              setMoreFor(null);
              if (refocus && !moreFor.at) moreFor.button.focus({ preventScroll: true });
            }}
          />
        );
      })()}
      {listPick && lists && (() => {
        const live = all.find((x) => x.id === listPick.m.id) ?? listPick.m;
        const held = new Set(lists.listsOf(live));
        return (
          <ListPicker
            serverId={lists.serverId}
            lists={lists.all}
            checked={held}
            pinned={!!lists.pins && held.has(lists.pins.id)}
            inDm={lists.inDm}
            actor={lists.actor}
            anchor={listPick.anchor}
            onToggle={(id, on) => lists.toggle(live, id, on)}
            onCreate={(name, visibility) => lists.create(live, name, visibility)}
            onClose={() => setListPick(null)}
          />
        );
      })()}
      {chipHold.overlay}
      {quickHold.overlay}
      {picker && (
        <EmojiPicker
          anchor={picker.anchor}
          style={{ right: 24, top: 8 }}
          superReactions
          onPick={(u, _custom, superReact, jumbo) => {
            const m = messages.find((x) => x.id === picker.id);
            // A jumbo pick also makes a reaction I already have jumbo (#714); a plain pick of one I have does nothing.
            if (m && jumbo) onReact(m.id, u, true, false, true);
            else if (m && !(me && m.reactions?.[u]?.includes(me))) onReact(m.id, u, true, superReact);
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
      {confirming && (
        <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && setConfirming(null)}>
          <div className="modal" role="alertdialog" aria-modal="true" aria-labelledby="del-title" style={{ maxWidth: 440 }}>
            <h2 id="del-title">Delete message</h2>
            <p style={{ margin: 0 }}>Are you sure? This removes it for everyone{confirming.attachments?.some((a) => a.kind === "file") ? ", along with its files" : ""}.</p>
            <div className="confirm-preview">
              <strong>{names.get(confirming.authorId) ?? "Someone"}</strong>
              <div>{plain(confirming.body) || (confirming.attachments?.length ? `${confirming.attachments.length} attachment${confirming.attachments.length > 1 ? "s" : ""}` : "")}</div>
            </div>
            <div className="row" style={{ justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setConfirming(null)}>Cancel</button>
              <button
                className="btn btn-danger"
                autoFocus
                onClick={() => {
                  onDelete(confirming);
                  setConfirming(null);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {lightbox && <Lightbox items={lightbox.as?.gallery?.length ? lightbox.as.gallery : [{ src: lightbox.src, original: lightbox.original, as: lightbox.as }]} start={lightbox.as?.gallery?.length ? (lightbox.as.index ?? 0) : 0} onClose={() => setLightbox(null)} />}
    </div>
    {(away || (!atBottom && newCount > 0)) && (
      <div className="jump-wrap">
        <button type="button" className={`jump-pill${newCount > 0 ? " has-new" : ""}`} onClick={jumpToPresent}>
          {newCount > 0 ? <span>{newCount > 99 ? "99+" : newCount} new message{newCount === 1 ? "" : "s"}</span> : <span>You're viewing older messages</span>}
          <span className="jump-action">
            Jump to present
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 5v14M6 13l6 6 6-6" /></svg>
          </span>
        </button>
      </div>
    )}
    {linkMenu && <LinkMenu target={linkMenu.target} at={linkMenu.at} onCopy={copyLink} onClose={() => setLinkMenu(null)} />}
    </ShortLinkContext.Provider>
    </MessageLinkTextContext.Provider>
  );
}

/** Discord-style inline editing: Enter saves, Shift+Enter is a new line, Esc cancels. */
function InlineEditor({ initial, onChange, onDone }: { initial: string; onChange: (text: string) => void; onDone: (body: string | null) => void }) {
  const [text, setText] = useState(initial);
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
  }, [text]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.selectionStart = el.selectionEnd = el.value.length;
    el.scrollIntoView({ block: "nearest" });
  }, []);
  return (
    <div className="inline-edit">
      <label className="sr-only" htmlFor="edit-box">Edit message</label>
      <div className="fl-field">
        <FormatBar
          area={ref}
          text={text}
          setValue={(v) => {
            setText(v);
            onChange(v);
          }}
        />
        <textarea
          id="edit-box"
          ref={ref}
          rows={1}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            onChange(e.target.value);
          }}
          onKeyDown={(e) => {
            if (
              formatShortcut(e, (v) => {
                setText(v);
                onChange(v);
              })
            )
              return;
            if (e.key === "Escape") {
              e.preventDefault();
              onDone(null);
            } else if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing && !isTouch()) {
              e.preventDefault();
              onDone(text);
            }
          }}
        />
        {/* After the textarea, so its ref is attached when the layer first copies its styles. */}
        <FormatLayer area={ref} text={text} />
      </div>
      {isTouch() ? (
        <div className="inline-edit-actions">
          <button type="button" className="btn btn-small" onClick={() => onDone(null)}>Cancel</button>
          <button type="button" className="btn btn-primary btn-small" onPointerDown={(e) => e.preventDefault()} onClick={() => onDone(text)}>Save</button>
        </div>
      ) : (
        <div className="inline-edit-hint">
          escape to <button type="button" className="link-btn" onClick={() => onDone(null)}>cancel</button> · enter to <button type="button" className="link-btn" onClick={() => onDone(text)}>save</button>
        </div>
      )}
    </div>
  );
}


/**
 * A forwarded message's copy of the original (Discord's Forward): "Forwarded", the text, attachments and previews as
 * they were when forwarded, and where it came from. The link goes back to the original; the page refuses it for
 * people who can't see that conversation (a DM shows only "Direct message", never who was in it).
 */
/**
 * A linked message, drawn for this viewer from what the room says they may see: who wrote it, where and when, and
 * a few lines of its text. A tap goes to it. "Message unavailable" when it's gone or not theirs to see.
 */
function LinkPreview({ link, ctx }: { link: MessageLinkRef; ctx: RowCtx }) {
  const host = ctx.links!;
  const [got, setGot] = useState<{ message: ChatMessage; rootId: string } | null | undefined>(undefined);
  useEffect(() => {
    let live = true;
    void host.resolve(link.messageId).then((r) => live && setGot(r));
    return () => {
      live = false;
    };
  }, [host, link.messageId]);
  if (got === undefined) return null;
  if (got === null) return <div className="msglink gone">Message unavailable</div>;
  const m = got.message;
  const name = ctx.channels?.byId.get(got.rootId);
  const where = `${name ? `#${name}` : "Direct message"}${m.channelId !== got.rootId ? " › thread" : ""}`;
  const source = m.body.trim() ? m.body : (m.forward?.body ?? "");
  const text = ctx.plain(markdownToPlain(source, { spoiler: SPOILER_MASK, time: formatStamp })).replace(/\s+/g, " ").trim();
  const files = (m.body.trim() ? m.attachments : (m.attachments ?? m.forward?.attachments))?.length ?? 0;
  const who = ctx.names.get(m.authorId) ?? "Someone";
  return (
    <button type="button" className="msglink" onClick={() => host.open(link)} title="Go to the message">
      <span className="msglink-head">
        <Avatar id={m.authorId} name={who} size={16} />
        <strong>{who}</strong>
        <span className="msglink-where">{where} · <time dateTime={new Date(m.ts).toISOString()}>{forwardFmt.format(m.ts)}</time></span>
      </span>
      {text && <span className="msglink-text">{text}</span>}
      {files > 0 && <span className="msglink-files">{files} {files === 1 ? "attachment" : "attachments"}</span>}
    </button>
  );
}

function ForwardBlock({ m, f, ctx }: { m: ChatMessage; f: Forward; ctx: RowCtx }) {
  const { names, me, act } = ctx;
  const name = ctx.channels?.byId.get(f.channelId);
  const where = f.dm ? "Direct message" : name ? `#${name}` : "#deleted-channel";
  const label = `${where}${f.stream !== f.channelId ? " › thread" : ""}`;
  // My own optimistic forward still points at the original's files; the room's copy arrives with the ack.
  const key = `${m.id}:fwd`;
  return (
    <div className="fwd">
      <div className="fwd-label"><ForwardIcon size={13} /> Forwarded</div>
      {f.body && (
        <div className={`body${onlyEmoji(f.body) ? " jumbo" : ""}`}>
          {hasMarkdown(f.body) ? (
            <Markdown source={f.body} text={(t) => <Body text={t} names={names} me={me} channels={ctx.channels} />} />
          ) : (
            <Body text={f.body} names={names} me={me} channels={ctx.channels} />
          )}
        </div>
      )}
      {f.attachments && <Attachments messageId={key} attachments={f.attachments} onOpen={(src, original) => act.current.lightbox(src, original)} />}
      {f.embeds && f.embeds.length > 0 && <Embeds messageId={key} embeds={f.embeds} onOpen={(src, original, as) => act.current.lightbox(src, original, as)} />}
      {act.current.openForward ? (
        <button type="button" className="fwd-from" onClick={() => act.current.openForward?.(f)} title="Go to the original message">
          <span>{label}</span> · <time dateTime={new Date(f.ts).toISOString()}>{forwardFmt.format(f.ts)}</time>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
        </button>
      ) : (
        <span className="fwd-from static"><span>{label}</span> · <time dateTime={new Date(f.ts).toISOString()}>{forwardFmt.format(f.ts)}</time></span>
      )}
    </div>
  );
}

function MenuItem({ icon, label, onClick, danger }: { icon: string; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button type="button" className={`menu-item${danger ? " danger" : ""}`} onClick={onClick}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{MENU_ICONS[icon]}</svg>
      {label}
    </button>
  );
}

export function SmileIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 14.5c1 1.2 2.1 1.8 3.5 1.8s2.5-.6 3.5-1.8M9 9.5h.01M15 9.5h.01" />
    </svg>
  );
}

/**
 * A picture opened full size, or a set of them (a post's pictures) to page through: swipe sideways on a phone, the
 * ‹ › buttons or ← → on a computer, with "2 / 4" at the top. Closes on a click outside the picture, the × in the
 * corner, or Escape (taken in the capture phase so the composer and the chat shortcuts don't also act on it); on a
 * phone also by dragging it down (a picture or a clip). A still picture zooms with a pinch or a double tap on a phone, and pans
 * while zoomed (lightboxGestures.ts). Back (a phone's back swipe, the Back button, Android's back key) closes it rather
 * than leaving the page: opening adds a history entry (backCloses.ts) that closing takes off again.
 * Focus moves to the × while it's open and goes back where it was after. A GIF (`as` with its size) plays large:
 * scaled up to fill the screen (at most 4× its own size, so tiny ones don't turn to mush), as a looping muted video
 * when it's video-format. It's drawn into <body>, not inside the chat: on a phone the chat is moved with transforms
 * (the panels slide it aside), and a transformed ancestor would pin a fixed overlay to it instead of the screen.
 */
export function Lightbox({ items, start, onClose }: { items: ViewerItem[]; start: number; onClose: () => void }) {
  const close = useRef(onClose);
  close.current = onClose;
  const box = useRef<HTMLDivElement>(null);
  const count = items.length;
  const [index, setIndex] = useState(() => clampPage(start, count));
  const at = useRef(index);
  at.current = index;
  // Which way the new picture slides in after a page turn.
  const [enter, setEnter] = useState<"next" | "prev" | null>(null);
  const [info, setInfo] = useState(false);
  const go = useCallback(
    (to: number) => {
      const next = clampPage(to, count);
      if (next === at.current) return;
      setEnter(next > at.current ? "next" : "prev");
      setInfo(false);
      setIndex(next);
    },
    [count],
  );
  const { src, original, as } = items[index]!;
  // The ⓘ panel (#379) is for one picture: `go` closes it, so turning the page, by swipe, button or key, never carries it over (#585).
  const photo = as?.photo;
  const infoOpen = !!photo && info;
  const toggleInfo = useCallback(() => setInfo((open) => !open), []);
  const canInfo = useRef(false);
  canInfo.current = !!photo;

  // One finger at a time: sideways turns the page (a set only), down closes, and a zoomed picture pans; two fingers
  // pinch a still picture, and two quick taps zoom it in and out (lightboxGestures.ts, on lightboxLogic.ts's rules).
  const zoomable = !as?.clip && !as?.video;
  const gestures = useViewerGestures({ box, count, index, zoomable, onClose: () => close.current(), onPage: go });

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        close.current();
      } else if ((e.key === "i" || e.key === "I") && canInfo.current && !e.altKey && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.repeat && !isEditable(document.activeElement)) {
        // "I" too, so Caps Lock works; never while a text box has focus (the composer behind the viewer): that is typing.
        e.preventDefault();
        e.stopPropagation();
        toggleInfo();
      } else if ((e.key === "ArrowLeft" || e.key === "ArrowRight") && count > 1 && !e.altKey && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        e.stopPropagation();
        go(at.current + (e.key === "ArrowRight" ? 1 : -1));
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      before?.focus?.({ preventScroll: true });
    };
  }, [count, go, toggleInfo]);

  // Back closes the viewer, not the page (backCloses.ts).
  useBackCloses(true, () => close.current());

  // The pictures either side load ahead, so a swipe lands on a picture rather than a blank.
  useEffect(() => {
    for (const i of [index - 1, index + 1]) {
      const it = items[i];
      if (it && !it.as?.clip && !it.as?.video) new Image().src = it.src;
    }
  }, [index, items]);

  // A GIF says whether it's a video (pictures pass only what Download saves).
  const gif = !!as && as.video !== undefined;
  // Width fits the screen keeping the GIF's shape (lightbox-gif in styles.css); the height follows from aspect-ratio.
  const size = as?.width && as.height
    ? ({ "--ar": (as.width / as.height).toFixed(4), "--most": `${as.width * 4}px`, aspectRatio: `${as.width} / ${as.height}` } as React.CSSProperties)
    : undefined;
  const kind = as?.clip ? "Video" : gif ? "GIF" : "Image";
  const slide = enter ? ` lightbox-enter-${enter}` : "";
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  return createPortal(
    <div
      ref={box}
      className={`overlay lightbox${count > 1 ? " is-set" : ""}`}
      onClick={onClose}
      {...gestures}
      role="dialog"
      aria-modal="true"
      aria-label={count > 1 ? `${kind} ${index + 1} of ${count}` : kind}
    >
      {as?.clip ? (
        <video key={index} className={slide.trim() || undefined} src={src} controls autoPlay playsInline onClick={stop} />
      ) : as?.video ? (
        <video key={index} className={`lightbox-gif${slide}`} src={src} style={size} autoPlay loop muted playsInline onClick={stop} />
      ) : (
        <img key={index} className={`${gif ? "lightbox-gif" : ""}${slide}`.trim() || undefined} src={src} alt="" style={size} onClick={stop} />
      )}
      {count > 1 && (
        <>
          <div className="lightbox-count" aria-hidden="true">{pageLabel(index, count)}</div>
          <RoundButton tone="media" className="lightbox-page lightbox-prev" aria-label="Previous" title="Previous (←)" disabled={index === 0} onClick={(e) => { e.stopPropagation(); go(index - 1); }}>
            <ChevronIcon size={20} />
          </RoundButton>
          <RoundButton tone="media" className="lightbox-page lightbox-next" aria-label="Next" title="Next (→)" disabled={index === count - 1} onClick={(e) => { e.stopPropagation(); go(index + 1); }}>
            <ChevronIcon size={20} />
          </RoundButton>
        </>
      )}
      <CloseButton className="lightbox-close" tone="media" aria-label="Close" title="Close (Esc)" autoFocus onClick={(e) => { e.stopPropagation(); onClose(); }} />
      {photo && (
        <RoundButton
          tone="media"
          className="lightbox-info"
          aria-label="Photo info"
          aria-pressed={infoOpen}
          aria-controls={infoOpen ? "lightbox-info-panel" : undefined}
          title="Photo info (I)"
          onClick={(e) => { e.stopPropagation(); toggleInfo(); }}
        >
          <InfoIcon size={20} />
        </RoundButton>
      )}
      {photo && infoOpen && (
        // Plain text only: the lines come from the photo's file, so they are never HTML, markdown or a link.
        <div id="lightbox-info-panel" className="lightbox-info-panel" role="status" onClick={stop}>
          {photo.title && <div className="lightbox-info-title">{photo.title}</div>}
          {photo.settings && <div className="lightbox-info-settings">{photo.settings}</div>}
          {photo.taken && <div className="lightbox-info-taken">{photo.taken}</div>}
        </div>
      )}
      <div className="lightbox-actions" onClick={stop}>
        <a href={original} target="_blank" rel="noreferrer" className="lightbox-open">Open original</a>
        {as?.download && (
          <button type="button" className="lightbox-download" onClick={() => void download([as.download!])}>
            Download
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** "Download image", "Download GIF", "Download video" or "Download 3 files". */
function downloadLabel(attachments: ChatMessage["attachments"]): string {
  const items = (attachments ?? []).filter((a) => a.kind !== "sticker" && downloadables([a]).length);
  if (items.length !== 1) return `Download ${items.length} files`;
  const a = items[0]!;
  return a.kind === "gif" ? "Download GIF" : a.kind === "file" && a.type.startsWith("video/") ? "Download video" : "Download image";
}
